'use client';

import { createParser, type EventSourceMessage } from 'eventsource-parser';

import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

import { resolveFriendlyErrorMessage } from './summary-utils';

type TokenEventPayload = { delta?: string; text?: string };
type DoneEventPayload = { text?: string; includedImages?: number };
type ErrorEventPayload = { message?: string; code?: string | null };
type StatusEventPayload = Record<string, unknown>;
type ErrorResponsePayload = { error?: string; code?: string | null };

interface StreamCallbacks {
  onComplete?: (payload: DoneEventPayload) => void;
  onError?: (payload: { message: string; code?: string | null }) => void;
  onStatus?: (payload: StatusEventPayload) => void;
  onToken?: (payload: TokenEventPayload) => void;
}

interface StreamConversationSummaryParameters {
  callbacks?: StreamCallbacks;
  canonicalTweetId: string;
  fetcher?: typeof fetch;
  includeImages: boolean;
  signal: AbortSignal;
  summaryImageCount: number;
}

function getPlainRecord<T extends Record<string, unknown>>(value: unknown): T | null {
  return isRecord<T>(value) ? value : null;
}

const parseErrorResponsePayload = (value: string): ErrorResponsePayload | null => {
  try {
    return getPlainRecord<ErrorResponsePayload>(JSON.parse(value));
  } catch {
    return null;
  }
};

const parseTokenPayload = (value: unknown): TokenEventPayload | null => {
  const record = getPlainRecord(value);
  if (!record) {
    return null;
  }
  const payload: TokenEventPayload = {};
  const deltaValue = getRecordValue(record, 'delta');
  if (typeof deltaValue === 'string') {
    payload.delta = deltaValue;
  }
  const textValue = getRecordValue(record, 'text');
  if (typeof textValue === 'string') {
    payload.text = textValue;
  }
  return payload;
};

const parseDonePayload = (value: unknown): DoneEventPayload | null => {
  const record = getPlainRecord(value);
  if (!record) {
    return null;
  }
  const payload: DoneEventPayload = {};
  const textValue = getRecordValue(record, 'text');
  if (typeof textValue === 'string') {
    payload.text = textValue;
  }
  const includedImagesValue = getRecordValue(record, 'includedImages');
  if (typeof includedImagesValue === 'number' && Number.isFinite(includedImagesValue)) {
    payload.includedImages = includedImagesValue;
  }
  return payload;
};

const parseErrorPayload = (value: unknown): ErrorEventPayload | null => {
  const record = getPlainRecord(value);
  if (!record) {
    return null;
  }
  const payload: ErrorEventPayload = {};
  const messageValue = getRecordValue(record, 'message');
  if (typeof messageValue === 'string') {
    payload.message = messageValue;
  }
  const codeValue = getRecordValue(record, 'code');
  if (typeof codeValue === 'string') {
    payload.code = codeValue;
  } else if (codeValue === null) {
    payload.code = null;
  }
  return payload;
};

const parseStatusPayload = (value: unknown): StatusEventPayload | null => getPlainRecord(value);

export async function streamConversationSummary({
  canonicalTweetId,
  includeImages,
  summaryImageCount,
  signal,
  fetcher = fetch,
  callbacks,
}: StreamConversationSummaryParameters): Promise<void> {
  const response = await fetcher(`/api/tweets/${canonicalTweetId}/conversation/summarize`, {
    method: 'POST',
    signal,
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      includeImages,
    }),
  });

  if (!(response.ok && response.body)) {
    const fallback = await response.text().catch(() => null);
    const parsed = fallback ? parseErrorResponsePayload(fallback) : null;
    const message = resolveFriendlyErrorMessage(parsed ?? fallback ?? undefined);
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createParser({
    onEvent: (event) => {
      processEvent(event);
    },
  });
  let aggregated = '';
  let streamErrored: boolean | undefined;

  const recordStreamError = (message: string, code?: string | null) => {
    streamErrored = true;
    callbacks?.onError?.({ message, code: code ?? null });
  };

  const processEvent = (event: EventSourceMessage) => {
    if (!event.data) {
      return;
    }

    try {
      const rawPayload: unknown = JSON.parse(event.data);
      const payloadRecord = parseStatusPayload(rawPayload);

      switch (event.event) {
        case 'token': {
          const tokenPayload = parseTokenPayload(payloadRecord ?? rawPayload);
          if (!tokenPayload) {
            return;
          }
          aggregated = appendToken(aggregated, tokenPayload);
          callbacks?.onToken?.(tokenPayload);

          break;
        }
        case 'done': {
          const donePayload = parseDonePayload(payloadRecord ?? rawPayload);
          if (!donePayload) {
            return;
          }
          aggregated = finalizeAggregation(aggregated, donePayload);
          const completedPayload = withDefaultIncludedImages(donePayload, includeImages, summaryImageCount);
          callbacks?.onComplete?.(completedPayload);

          break;
        }
        case 'error': {
          const errorPayload = parseErrorPayload(payloadRecord ?? rawPayload);
          const messageSource = errorPayload ?? payloadRecord ?? rawPayload;
          const message = resolveFriendlyErrorMessage(messageSource ?? undefined);
          const codeValue = payloadRecord ? getRecordValue(payloadRecord, 'code') : undefined;
          const codeFromRecord = typeof codeValue === 'string' ? codeValue : null;
          const code = errorPayload?.code ?? codeFromRecord ?? null;
          recordStreamError(message, code);

          break;
        }
        case 'status': {
          if (payloadRecord) {
            callbacks?.onStatus?.(payloadRecord);
          }

          break;
        }
        case undefined: {
          break;
        }
        default: {
          break;
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to summarize conversation.';
      recordStreamError(resolveFriendlyErrorMessage(message));
    }
  };

  const pump = async (): Promise<void> => {
    const { done, value } = await reader.read();
    if (done) {
      return;
    }
    parser.feed(decoder.decode(value, { stream: true }));
    await pump();
  };

  await pump();

  if (!streamErrored && aggregated.length === 0) {
    throw new Error('Summarizer returned an empty response.');
  }
}

function appendToken(aggregated: string, payload: TokenEventPayload): string {
  if (typeof payload.delta === 'string') {
    return aggregated + payload.delta;
  }
  if (typeof payload.text === 'string' && payload.text.length >= aggregated.length) {
    return payload.text;
  }
  return aggregated;
}

function finalizeAggregation(aggregated: string, payload: DoneEventPayload): string {
  if (typeof payload.text === 'string' && payload.text.length > 0) {
    return payload.text;
  }
  return aggregated;
}

function withDefaultIncludedImages(
  payload: DoneEventPayload,
  includeImages: boolean,
  summaryImageCount: number
): DoneEventPayload {
  const text = typeof payload.text === 'string' ? payload.text : undefined;
  let includedImages: number;
  if (typeof payload.includedImages === 'number') {
    includedImages = payload.includedImages;
  } else if (includeImages) {
    includedImages = summaryImageCount;
  } else {
    includedImages = 0;
  }
  const completedPayload: DoneEventPayload = { includedImages };
  if (text !== undefined) {
    completedPayload.text = text;
  }
  return completedPayload;
}
